import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Chat from "@/components/Chat";
import PhotoGallery from "@/components/PhotoGallery";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MapPin, Ship, Zap, Star, ArrowLeft, CheckCircle2, PlayCircle, Trash2, Award,
  CreditCard, ShieldCheck, ShieldAlert, Lock, PartyPopper, FileText, ClipboardCheck, Hourglass
} from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  open: { text: "Abierta · esperando ofertas", cls: "bg-[#EFE9E1] text-[#0B1D3A]" },
  accepted: { text: "Oferta aceptada", cls: "bg-[#0B1D3A] text-white" },
  in_progress: { text: "En curso", cls: "bg-[#FF6B57] text-white" },
  completed: { text: "Completada", cls: "bg-emerald-600 text-white" },
  cancelled: { text: "Cancelada", cls: "bg-gray-300 text-gray-800" },
};

function ProgressStepper({ req }) {
  // Steps: Publicada, Ofertas, Aceptada, Pagada (garantía), En curso, Aprobada, Pago liberado
  const steps = [
    { key: "published", label: "Publicada", done: true, icon: FileText },
    { key: "offers", label: "Ofertas recibidas", done: (req.offers_count || 0) > 0, icon: ClipboardCheck },
    { key: "accepted", label: "Oferta aceptada", done: ["accepted", "in_progress", "completed"].includes(req.status), icon: CheckCircle2 },
    { key: "paid", label: "Pago en garantía", done: ["in_escrow", "released"].includes(req.payment_status), icon: Lock },
    { key: "inprogress", label: "En curso", done: ["in_progress", "completed"].includes(req.status), icon: PlayCircle },
    { key: "approved", label: "Aprobado por cliente", done: req.status === "completed", icon: Award },
    { key: "released", label: "Pago liberado", done: req.payment_status === "released", icon: PartyPopper },
  ];
  const activeIdx = steps.findIndex((s) => !s.done);
  const currentIdx = activeIdx === -1 ? steps.length - 1 : activeIdx;

  return (
    <div className="hub-card p-6">
      <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mb-4">Progreso del trabajo</div>
      <div className="relative">
        <div className="absolute left-4 top-4 bottom-4 w-px bg-[#0B1D3A]/10" />
        <div className="space-y-4">
          {steps.map((s, i) => {
            const active = i === currentIdx && !s.done;
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex items-center gap-4 relative">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 relative z-10 border-2 ${s.done ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : active ? "bg-white border-[#FF6B57] text-[#FF6B57]" : "bg-white border-[#0B1D3A]/20 text-[#0B1D3A]/40"}`}>
                  <Icon size={13}/>
                </div>
                <div className={`text-sm font-display ${s.done ? "text-[#0B1D3A] font-semibold" : active ? "text-[#FF6B57] font-semibold" : "text-[#5C6B7D]"}`}>
                  {s.label}
                  {active && <span className="ml-2 text-xs italic normal-case font-normal">en curso</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [offers, setOffers] = useState([]);
  const [services, setServices] = useState([]);
  const [offerForm, setOfferForm] = useState({ price: "", duration_hours: "", available_date: "", message: "" });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [disputeForm, setDisputeForm] = useState({ reason: "", detail: "" });
  const [offerOpen, setOfferOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [dispOpen, setDispOpen] = useState(false);

  const load = async () => {
    try {
      const [r, o] = await Promise.all([api.get(`/requests/${id}`), api.get(`/requests/${id}/offers`)]);
      setReq(r.data); setOffers(o.data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    load();
    api.get("/services").then(r => setServices(r.data));
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [id]);

  if (!req) return <div className="hub-cream min-h-screen"><Navbar/><div className="p-20 text-center text-[#5C6B7D]">Cargando...</div></div>;

  const svcName = (id) => services.find(s => s.id === id)?.name || id;
  const st = STATUS[req.status] || STATUS.open;
  const isOwner = user?.role === "owner" && user?.id === req.owner_id;
  const isPro = user?.role === "professional";
  const isAssignedPro = isPro && user?.id === req.professional_id;
  const isAdmin = user?.role === "admin";
  const myOffer = offers.find(o => o.professional_id === user?.id);
  const chatOpen = (req.status === "accepted" || req.status === "in_progress" || req.status === "completed") && (isOwner || isAssignedPro || isAdmin);

  const sendOffer = async () => {
    if (!offerForm.price || !offerForm.duration_hours) { toast.error("Precio y duración obligatorios"); return; }
    try {
      await api.post(`/requests/${id}/offers`, {
        price: parseFloat(offerForm.price),
        duration_hours: parseFloat(offerForm.duration_hours),
        available_date: offerForm.available_date || null,
        message: offerForm.message,
      });
      toast.success("Oferta enviada");
      setOfferOpen(false);
      setOfferForm({ price: "", duration_hours: "", available_date: "", message: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const action = async (offerId, act) => {
    try {
      await api.patch(`/offers/${offerId}`, { action: act });
      toast.success(act === "accept" ? "Oferta aceptada. Ahora deposita el pago para arrancar." : "Hecho.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const payEscrow = async () => {
    try {
      const { data } = await api.post(`/requests/${id}/pay`, { origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("No se pudo iniciar el pago");
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const startJob = async () => {
    try { await api.post(`/requests/${id}/start`); toast.success("Trabajo iniciado."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const proMarkReady = async () => {
    try { await api.post(`/requests/${id}/complete`); toast.success("Marcado como listo. El cliente confirmará."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const ownerApprove = async () => {
    try { await api.post(`/requests/${id}/complete`); toast.success("¡Trabajo aprobado! Pago liberado al profesional."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sendReview = async () => {
    try {
      await api.post(`/requests/${id}/review`, reviewForm);
      toast.success("Gracias por tu valoración");
      setRevOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openDispute = async () => {
    if (!disputeForm.reason) { toast.error("Elige un motivo"); return; }
    try {
      await api.post(`/requests/${id}/dispute`, disputeForm);
      toast.success("Incidencia abierta. El equipo de MarinaHub la revisará.");
      setDispOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar esta solicitud? Se borrarán las ofertas asociadas.")) return;
    try { await api.delete(`/requests/${id}`); toast.success("Solicitud eliminada"); nav(-1); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="min-h-screen hub-cream">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-[#5C6B7D] mb-6 font-display" data-testid="back-btn">
          <ArrowLeft size={14}/> Volver
        </button>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 space-y-6">
            <div className="hub-card p-8">
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <span className={`hub-pill ${st.cls}`}>{st.text}</span>
                {req.urgent && <span className="hub-pill bg-[#FF6B57]/10 text-[#FF6B57]"><Zap size={11}/> Urgente</span>}
                <span className="hub-pill hub-sand text-[#0B1D3A]">{svcName(req.service_type)}</span>
                {req.payment_status === "in_escrow" && <span className="hub-pill bg-emerald-100 text-emerald-800"><Lock size={11}/> Pago en garantía</span>}
                {req.payment_status === "released" && <span className="hub-pill bg-emerald-600 text-white"><PartyPopper size={11}/> Pago liberado</span>}
                {req.has_dispute && <span className="hub-pill bg-red-100 text-red-700"><ShieldAlert size={11}/> Incidencia abierta</span>}
              </div>
              <h1 className="font-serif text-4xl text-[#0B1D3A] tracking-tight">{req.boat_name || "Solicitud"} · {req.boat_size}</h1>
              <div className="mt-3 text-[#5C6B7D] flex items-center gap-2 flex-wrap"><MapPin size={14}/> {req.port} · {req.comarca && `${req.comarca} · `}{req.province}</div>
              {req.notes && <p className="mt-6 text-[#0B1D3A] leading-relaxed">{req.notes}</p>}

              {req.final_price && (
                <div className="mt-6 flex items-center gap-4 p-4 rounded-xl bg-[#FBF9F5] border border-[#0B1D3A]/10">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Precio acordado</div>
                    <div className="font-serif text-3xl hub-coral">{req.final_price}€</div>
                  </div>
                  <div className="flex-1 text-sm text-[#0B1D3A]/70">
                    Profesional: <strong>{req.professional_name}</strong>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 mt-6">
                {/* Pro sends offer */}
                {isPro && req.status === "open" && !myOffer && (
                  <Dialog open={offerOpen} onOpenChange={setOfferOpen}>
                    <DialogTrigger asChild>
                      <Button className="hub-btn-primary" data-testid="send-offer-btn">Enviar oferta</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-serif text-3xl">Tu oferta</DialogTitle></DialogHeader>
                      <div className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Precio (€)</Label><Input type="number" value={offerForm.price} onChange={(e) => setOfferForm({ ...offerForm, price: e.target.value })} data-testid="offer-price"/></div>
                          <div><Label>Duración (horas)</Label><Input type="number" step="0.5" value={offerForm.duration_hours} onChange={(e) => setOfferForm({ ...offerForm, duration_hours: e.target.value })} data-testid="offer-duration"/></div>
                        </div>
                        <div><Label>Fecha disponible</Label><Input type="date" value={offerForm.available_date} onChange={(e) => setOfferForm({ ...offerForm, available_date: e.target.value })} data-testid="offer-date"/></div>
                        <div><Label>Mensaje</Label><Textarea placeholder="Presenta tu propuesta..." value={offerForm.message} onChange={(e) => setOfferForm({ ...offerForm, message: e.target.value })} data-testid="offer-msg"/></div>
                        <Button onClick={sendOffer} className="hub-btn-primary w-full" data-testid="offer-submit">Enviar oferta</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Owner pays escrow via Stripe */}
                {isOwner && req.status === "accepted" && req.payment_status !== "in_escrow" && (
                  <Button onClick={payEscrow} className="hub-btn-coral hub-btn-primary gap-2" data-testid="pay-escrow-btn">
                    <CreditCard size={16}/> Pagar {req.final_price}€ con tarjeta
                  </Button>
                )}

                {/* Pro starts once paid */}
                {isAssignedPro && req.status === "accepted" && req.payment_status === "in_escrow" && (
                  <Button onClick={startJob} className="hub-btn-primary gap-2" data-testid="start-job-btn"><PlayCircle size={16}/> Empezar trabajo</Button>
                )}
                {isAssignedPro && req.status === "accepted" && req.payment_status !== "in_escrow" && (
                  <div className="hub-pill bg-amber-100 text-amber-800"><Hourglass size={11}/> Esperando pago del cliente</div>
                )}

                {/* Pro marks ready */}
                {isAssignedPro && req.status === "in_progress" && !req.awaiting_approval && (
                  <Button onClick={proMarkReady} className="hub-btn-coral hub-btn-primary gap-2" data-testid="mark-ready-btn"><CheckCircle2 size={16}/> Marcar como listo</Button>
                )}
                {isAssignedPro && req.awaiting_approval && (
                  <div className="hub-pill bg-amber-100 text-amber-800"><Hourglass size={11}/> Esperando confirmación del cliente</div>
                )}

                {/* Owner approves */}
                {isOwner && req.status === "in_progress" && (
                  <Button onClick={ownerApprove} className="hub-btn-coral hub-btn-primary gap-2" data-testid="approve-job-btn"><PartyPopper size={16}/> Aprobar y liberar pago</Button>
                )}

                {isOwner && req.status === "completed" && (
                  <Dialog open={revOpen} onOpenChange={setRevOpen}>
                    <DialogTrigger asChild>
                      <Button className="hub-btn-primary gap-2" data-testid="review-btn"><Award size={16}/> Valorar profesional</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-serif text-3xl">Valora el trabajo</DialogTitle></DialogHeader>
                      <div className="space-y-3 mt-2">
                        <div className="flex gap-1">
                          {[1,2,3,4,5].map(n => (
                            <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`star-${n}`}>
                              <Star size={32} className={n <= reviewForm.rating ? "fill-[#FF6B57] text-[#FF6B57]" : "text-[#0B1D3A]/20"}/>
                            </button>
                          ))}
                        </div>
                        <Textarea placeholder="Comenta cómo fue la experiencia" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} data-testid="review-comment"/>
                        <Button onClick={sendReview} className="hub-btn-primary w-full" data-testid="review-submit">Enviar valoración</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Dispute */}
                {(isOwner || isAssignedPro) && ["in_progress", "accepted", "completed"].includes(req.status) && !req.has_dispute && (
                  <Dialog open={dispOpen} onOpenChange={setDispOpen}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" className="text-red-500 gap-2" data-testid="dispute-btn"><ShieldAlert size={14}/> Abrir incidencia</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-serif text-2xl">Reportar problema</DialogTitle></DialogHeader>
                      <div className="space-y-3 mt-2">
                        <div>
                          <Label>Motivo</Label>
                          <select value={disputeForm.reason} onChange={(e) => setDisputeForm({ ...disputeForm, reason: e.target.value })} className="w-full mt-1 h-10 border border-[#0B1D3A]/15 rounded-md px-3" data-testid="dispute-reason">
                            <option value="">Elige un motivo</option>
                            <option value="quality">Calidad del trabajo</option>
                            <option value="no_show">No se presentó</option>
                            <option value="scope">Alcance distinto al pactado</option>
                            <option value="damage">Daños al barco</option>
                            <option value="other">Otro</option>
                          </select>
                        </div>
                        <Textarea placeholder="Detalles" value={disputeForm.detail} onChange={(e) => setDisputeForm({ ...disputeForm, detail: e.target.value })} data-testid="dispute-detail"/>
                        <Button onClick={openDispute} className="hub-btn-primary w-full" data-testid="dispute-submit">Abrir incidencia</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {isOwner && req.status === "completed" && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requests/${id}/invoice`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 border-[#0B1D3A] text-[#0B1D3A] font-display font-semibold hover:bg-[#0B1D3A] hover:text-white transition-colors" data-testid="invoice-btn">
                    <FileText size={16}/> Descargar factura PDF
                  </a>
                )}
                {isAssignedPro && req.status === "completed" && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/requests/${id}/invoice`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 border-[#0B1D3A] text-[#0B1D3A] font-display font-semibold hover:bg-[#0B1D3A] hover:text-white transition-colors" data-testid="pro-invoice-btn">
                    <FileText size={16}/> Mi factura
                  </a>
                )}

                {(isOwner || isAdmin) && ["open", "cancelled", "completed"].includes(req.status) && (
                  <Button variant="ghost" onClick={remove} className="text-red-500 gap-2 ml-auto" data-testid="delete-req-btn"><Trash2 size={14}/> Eliminar</Button>
                )}
              </div>
            </div>

            {/* Owner - offers list */}
            {isOwner && (
              <div>
                <h2 className="font-serif text-2xl text-[#0B1D3A] mb-3">Ofertas recibidas ({offers.filter(o => o.status === "pending").length})</h2>
                <div className="space-y-3">
                  {offers.length === 0 && <div className="hub-card p-8 text-center text-[#5C6B7D]">Aún no hay ofertas. Suele tardar entre 15 min y 24h.</div>}
                  {offers.map((o) => (
                    <div key={o.id} className="hub-card p-5" data-testid={`offer-item-${o.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full hub-sand flex items-center justify-center font-serif text-lg">{o.professional_name[0]}</div>
                            <div>
                              <button onClick={(e) => { e.stopPropagation(); nav(`/pro/profile/${o.professional_id}`); }} className="font-display font-semibold hover:underline text-left" data-testid={`view-pro-${o.professional_id}`}>{o.professional_name}</button>
                              <div className="text-xs text-[#5C6B7D] flex items-center gap-1"><Star size={11} className="fill-[#FF6B57] text-[#FF6B57]"/> {o.professional_rating?.toFixed(1) || "—"} · {o.professional_jobs} trabajos</div>
                            </div>
                          </div>
                          {o.message && <p className="mt-3 text-sm text-[#0B1D3A]/90">{o.message}</p>}
                        </div>
                        <div className="text-right">
                          <div className="font-serif text-3xl hub-coral">{o.price}€</div>
                          <div className="text-xs text-[#5C6B7D]">{o.duration_hours}h estimadas</div>
                        </div>
                      </div>
                      {o.status === "pending" && req.status === "open" && (
                        <div className="flex gap-2 mt-4">
                          <Button onClick={() => action(o.id, "accept")} className="hub-btn-primary flex-1" data-testid={`accept-${o.id}`}>Aceptar</Button>
                          <Button variant="ghost" onClick={() => action(o.id, "reject")} className="flex-1" data-testid={`reject-${o.id}`}>Rechazar</Button>
                        </div>
                      )}
                      {o.status === "accepted" && <div className="mt-3 hub-pill bg-emerald-100 text-emerald-800">Aceptada</div>}
                      {o.status === "rejected" && <div className="mt-3 hub-pill bg-gray-100 text-gray-600">Rechazada</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isPro && myOffer && (
              <div className="hub-card p-5">
                <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display">Tu oferta</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="font-serif text-2xl">{myOffer.price}€ · {myOffer.duration_hours}h</div>
                  <span className={`hub-pill ${{pending:"bg-[#EFE9E1] text-[#0B1D3A]", accepted:"bg-emerald-600 text-white", rejected:"bg-gray-200 text-gray-600"}[myOffer.status] || ""}`}>
                    {myOffer.status === "pending" ? "Pendiente" : myOffer.status === "accepted" ? "Aceptada" : "Rechazada"}
                  </span>
                </div>
                {myOffer.status === "pending" && <Button variant="ghost" onClick={() => action(myOffer.id, "withdraw")} className="mt-3 text-red-500" data-testid="withdraw-offer-btn">Retirar oferta</Button>}
              </div>
            )}
          </div>

          {/* Right column */}
          <aside className="md:col-span-5 space-y-4">
            <ProgressStepper req={req}/>
            {(req.status === "in_progress" || req.status === "completed") && (isOwner || isAssignedPro || isAdmin) && (
              <PhotoGallery requestId={id} canUpload={isAssignedPro && req.status === "in_progress"} />
            )}
            {chatOpen ? (
              <div>
                <h2 className="font-serif text-2xl text-[#0B1D3A] mb-3">Chat</h2>
                <Chat requestId={id}/>
              </div>
            ) : (
              <div className="hub-card p-6">
                <h3 className="font-serif text-xl">El chat se abre al aceptar una oferta</h3>
                <p className="text-sm text-[#5C6B7D] mt-2">Todo lo que se comparte pasa por MarinaHub. Sin números, sin emails: solo mensajes seguros y con historial.</p>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
